import { API_URLS } from '../config';
import { ROLE_IDS as CENTRALIZED_ROLE_IDS, CANDIDATE_TYPE_ROLES as CENTRALIZED_CANDIDATE_TYPE_ROLES, CandidateTypeId } from '../constants/roles';

export interface UserRole {
  id: string;
  name: string;
  description: string;
}

// Cache for user roles to avoid unnecessary API calls
const roleCache: Record<string, { roles: UserRole[]; timestamp: number }> = {};
const CACHE_DURATION = 30 * 1000; // Reduce to 30 seconds

// Track in-flight requests to prevent duplicate calls
const inFlightRequests: Record<string, AbortController> = {};

// Re-export centralized role IDs for backward compatibility
export const ROLE_IDS = {
  INTERVIEWER: CENTRALIZED_ROLE_IDS.INTERVIEWER,
  RECRUITER: CENTRALIZED_ROLE_IDS.MANAGER,  // Previously called "Manager"
  MANAGER: CENTRALIZED_ROLE_IDS.MANAGER,  // Alias for backward compatibility - TODO: Remove after full migration
} as const;

// Note: For candidate type roles, use the centralized CANDIDATE_TYPE_ROLES from constants/roles.ts
export const CANDIDATE_TYPE_ROLES = CENTRALIZED_CANDIDATE_TYPE_ROLES;

export type CandidateType = CandidateTypeId;

export const clearUserRoleCache = (userId: string) => {
  delete roleCache[userId];
  delete inFlightRequests[userId];
};

export const getCurrentRole = async (
  userId: string,
  getAccessTokenSilently: () => Promise<string>
): Promise<'interviewer' | 'recruiter' | null> => {
  // Check cache first
  const cached = roleCache[userId];
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    // Determine the user's role from cached roles
    if (cached.roles.some(role => role.id === ROLE_IDS.RECRUITER)) {
      return 'recruiter';
    } else if (cached.roles.some(role => role.id === ROLE_IDS.INTERVIEWER)) {
      return 'interviewer';
    }
    return null;
  }

  try {
    const roles = await getUserRoles(userId, getAccessTokenSilently);

    // Determine the user's role
    let role: 'interviewer' | 'recruiter' | null = null;
    if (roles.some(r => r.id === ROLE_IDS.RECRUITER)) {
      role = 'recruiter';
    } else if (roles.some(r => r.id === ROLE_IDS.INTERVIEWER)) {
      role = 'interviewer';
    }

    return role;
  } catch (error) {
    console.error('Error fetching user role:', error);
    return null;
  }
};

export const getUserRoles = async (
  userId: string,
  getAccessTokenSilently: () => Promise<string>,
  forceRefresh = false
): Promise<UserRole[]> => {
  try {
    // Check cache first if not forcing refresh
    if (!forceRefresh) {
      const cachedData = roleCache[userId];
      if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
        console.log('Using cached roles for user:', userId);
        return cachedData.roles;
      }
    } else {
      console.log('Force refresh requested, ignoring cache for user:', userId);
      // Clear the cache when force refresh is requested
      delete roleCache[userId];
    }

    // Clear any existing in-flight request for this user
    if (userId in inFlightRequests) {
      console.log('Cancelling existing request for user:', userId);
      inFlightRequests[userId].abort();
      delete inFlightRequests[userId];
    }

    // Create new abort controller for this request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    inFlightRequests[userId] = controller;

    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_URLS.BASE}/auth/user-roles/${userId}?force_refresh=${forceRefresh}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    delete inFlightRequests[userId];

    if (!response.ok) {
      throw new Error(`Failed to fetch roles: ${response.statusText}`);
    }

    const data = await response.json();
    const roles = data.roles || [];
    
    // Update cache
    roleCache[userId] = {
      roles,
      timestamp: Date.now(),
    };

    return roles;
  } catch (error) {
    // If the request was aborted, try to use cached data
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Request timed out, using cached data if available');
      const cachedData = roleCache[userId];
      if (cachedData) {
        return cachedData.roles;
      }
    }
    
    // If we have cached data and it's not too old (within 5 minutes), use it
    const cachedData = roleCache[userId];
    if (cachedData && Date.now() - cachedData.timestamp < 5 * 60 * 1000) {
      console.log('Using cached roles due to error');
      return cachedData.roles;
    }
    
    throw error;
  }
};

export const assignRole = async (
  userId: string,
  roleId: string,
  getAccessTokenSilently: () => Promise<string>
): Promise<void> => {
  try {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_URLS.BASE}/auth/assign-role`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ 
        user_id: userId,
        role_id: roleId 
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to assign role: ${errorData.detail || response.statusText}`);
    }

    // Clear the role cache for this user
    clearUserRoleCache(userId);
  } catch (error) {
    console.error('Error assigning role:', error);
    throw error;
  }
};

export const removeRole = async (
  userId: string,
  roleId: string,
  getAccessTokenSilently: () => Promise<string>
): Promise<void> => {
  try {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_URLS.BASE}/auth/remove-role/${userId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        roles: [roleId]
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to remove role: ${errorData.detail || response.statusText}`);
    }

    // Clear the role cache for this user
    clearUserRoleCache(userId);
  } catch (error) {
    console.error('Error removing role:', error);
    throw error;
  }
};

export const switchRole = async (
  userId: string, 
  newRoleId: string,
  getAccessTokenSilently: () => Promise<string>
): Promise<'interviewer' | 'manager'> => {
  try {
    // First get current roles
    const currentRoles = await getUserRoles(userId, getAccessTokenSilently);
    
    // Remove all existing roles
    for (const role of currentRoles) {
      await removeRole(userId, role.id, getAccessTokenSilently);
    }
    
    try {
      // Assign new role
      await assignRole(userId, newRoleId, getAccessTokenSilently);
    } catch (error) {
      // If we get a 409 (Conflict) error, the role is already assigned
      // This is fine, we can continue
      if (error instanceof Error && error.message.includes('409')) {
        console.log('Role already assigned, continuing...');
      } else {
        // For other errors, we should rethrow
        throw error;
      }
    }

    // Return the new role type
    return newRoleId === ROLE_IDS.MANAGER ? 'manager' : 'interviewer';
  } catch (error) {
    console.error('Error switching role:', error);
    throw error;
  }
}; 